import { useEditor, Node, mergeAttributes } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect } from 'react';
import { RichTextEditor } from '@mantine/tiptap';

/**
 * Minimaler Bild-Node: das StarterKit kennt keine <img>-Tags — ohne diesen Node
 * verwirft der Editor eingefügte Bilder beim setContent kommentarlos (der Upload
 * lief durch, aber die Inline-Vorschau verschwand aus dem Kommentar). Bewusst
 * kein @tiptap/extension-image als neue Dependency: wir brauchen nur Parsen und
 * Rendern von src/alt, keine Bildbearbeitung.
 */
const InlineImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'img[src]' }];
  },
  renderHTML({ HTMLAttributes }) {
    // max-width wandert mit ins gespeicherte HTML — so bleibt das Bild auch in
    // Weclapp, Kundenportal und Mails auf Container-Breite begrenzt.
    return ['img', mergeAttributes(HTMLAttributes, { style: 'max-width: 100%; height: auto;' })];
  },
});

interface WysiwygEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  /**
   * Bild aus der Zwischenablage (Screenshot) — wird mit derselben Upload-Logik
   * verarbeitet wie der „Bild einfügen"-Button. Ohne Handler bleibt das Einfügen
   * dem Standardverhalten überlassen.
   */
  onImagePaste?: (file: File) => void;
}

export function WysiwygEditor({
  value,
  onChange,
  placeholder,
  minHeight = 120,
  onImagePaste,
}: WysiwygEditorProps) {
  // Screenshots aus Zwischenablage/Finder heißen oft „image.png" oder gar nichts —
  // sprechender Name, damit die Datei am Ticket wiederauffindbar bleibt.
  const withScreenshotName = (file: File) =>
    file.name && file.name !== 'image.png'
      ? file
      : new File([file], `screenshot-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`, {
          type: file.type,
        });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: placeholder ?? 'Beschreibung…' }),
      InlineImage,
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      // Die contenteditable-Fläche selbst auf Mindesthöhe ziehen: sonst ist nur die
      // erste (Text-)Zeile klickbar und ein Klick in den freien Bereich darunter
      // fokussiert den Editor nicht.
      attributes: {
        style: `min-height: ${Math.max(minHeight - 24, 60)}px`,
      },
      handlePaste: (_view, event) => {
        if (!onImagePaste) return false;
        const items = Array.from(event.clipboardData?.items ?? []);
        const imageItem = items.find((i) => i.type.startsWith('image/'));
        if (!imageItem) return false;
        const file = imageItem.getAsFile();
        if (!file) return false;
        onImagePaste(withScreenshotName(file));
        event.preventDefault();
        return true;
      },
      // Bilddateien per Drag & Drop in den Editor ziehen — gleicher Upload-Weg wie
      // Einfügen/„Bild einfügen". Ohne diesen Handler übernimmt der Browser den Drop
      // und öffnet das Bild als neuen Tab.
      handleDrop: (_view, event) => {
        if (!onImagePaste) return false;
        const files = Array.from(event.dataTransfer?.files ?? []).filter((f) =>
          f.type.startsWith('image/'),
        );
        if (files.length === 0) return false;
        files.forEach((f) => onImagePaste(withScreenshotName(f)));
        event.preventDefault();
        return true;
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <RichTextEditor editor={editor}>
      <RichTextEditor.Toolbar sticky>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
          <RichTextEditor.Strikethrough />
          <RichTextEditor.ClearFormatting />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H1 />
          <RichTextEditor.H2 />
          <RichTextEditor.H3 />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Link />
          <RichTextEditor.Unlink />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Undo />
          <RichTextEditor.Redo />
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>
      <RichTextEditor.Content
        style={{ minHeight, cursor: 'text' }}
        onClick={() => {
          // Klick in den Randbereich (außerhalb der ProseMirror-Fläche) fokussiert
          // ebenfalls — ans Ende, wie man es von Textfeldern erwartet.
          if (editor && !editor.isFocused) editor.commands.focus('end');
        }}
      />
    </RichTextEditor>
  );
}
